import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { EditDialog as EditAnnouncementDialog } from "../Management/AnnouncementManagement/EditAnnouncementDialog/EditAnnouncementDialog";
import { EditDialog as EditEventDialog} from "../Management/EventsManagement/EditEventDialog/EditEventDialog";
import { EditDialog as EditKnowledgeItemDialog } from "../Management/KnowledgeItemsManagement/EditKnowledgeItemDialog/EditKnowledgeItemDialog";
import { isNotNil } from "client/Misc/utils";

type Options = {
	kind: HubItemKind;
	selectedTemplateId?: number;
	preferredCategoryId?: number | null;
	onClose(wasAnyCategoryCreated: boolean): void;
	onAnyCategoryCreatedChange(wasAnyCategoryCreated: boolean): void;
	onSave?(): void;
};

export function useOpenEditHubDialog(): [
	element: ReactElement | null,
	open: (options: Options) => void
] {
	const [state, setState] = useState<State>({
		isOpen: false
	});

	const element = useMemo(
		(): ReactElement | null => {
			if (!state.isOpen) {
				return null;
			}

			const {
				kind,
				onAnyCategoryCreatedChange,
				onClose,
				selectedTemplateId,
				onSave,
				preferredCategoryId
			} = state.options;

			const backHandler = (wasAnyCategoryCreated: boolean): void => {
				onAnyCategoryCreatedChange(wasAnyCategoryCreated);
				setState({ isOpen: false });
			};

			switch (kind) {
				case HubItemKind.Announcement:
					return (
						<EditAnnouncementDialog
							preferredCategoryId={preferredCategoryId}
							onClose={onClose}
							onSave={onSave}
							onBack={backHandler}
							mode={
								isNotNil(selectedTemplateId)
									? {
										kind: "new-from-template",
										templateId: selectedTemplateId
									}
									: { kind: "new" }
							}
						/>
					);
				case HubItemKind.Event:
					return (
						<EditEventDialog
							mode={
								isNotNil(selectedTemplateId)
									? {
										kind: "new-from-template",
										templateId: selectedTemplateId
									}
									: { kind: "new" }
							}
							preferredCategoryId={preferredCategoryId}
							onClose={onClose}
							onSave={onSave}
							onBack={backHandler}
						/>
					);
				case HubItemKind.KnowledgeItem:
					return (
						<EditKnowledgeItemDialog
							mode={isNotNil(selectedTemplateId)
								? {
									kind: "new-from-template",
									templateId: selectedTemplateId
								}
								: { kind: "new" }
							}
							preferredCategoryId={preferredCategoryId}
							onClose={onClose}
							onSave={onSave}
							onBack={backHandler}
						/>
					);
				default:
					never(kind);
			}
		},
		[state]
	);

	const openHandler = useCallback(
		(options: Options): void => {
			setState({
				isOpen: true,
				options
			});
		},
		[]
	);

	return [
		element,
		openHandler
	];
}

type State = {
	isOpen: false;
} | {
	isOpen: true;
	options: Options;
};